/**
 * MonthView — monthly calendar where each day is rendered as a soft orb,
 * not as a cell. Out-of-month days fade away so the active month feels
 * like a contained constellation.
 */
import { useMemo } from "react";
import { ChevronLeft, ChevronRight, MessageCircle } from "lucide-react";
import {
  calendarColor,
  monthMatrix,
  toISO,
  MONTHS_FULL,
} from "@/lib/calendar";

export default function MonthView({ focusDate, onChangeFocus, days, selectedDate, onSelect }) {
  const year = focusDate.getFullYear();
  const month = focusDate.getMonth();
  const today = useMemo(() => toISO(new Date()), []);

  const matrix = useMemo(() => monthMatrix(year, month), [year, month]);
  const byIso = useMemo(() => new Map((days || []).map((d) => [d.date, d])), [days]);

  const goto = (delta) => {
    const next = new Date(focusDate);
    next.setMonth(month + delta);
    next.setDate(1);
    onChangeFocus(next);
  };

  const headerLabel = `${MONTHS_FULL[month]} ${year}`;

  return (
    <div
      className="relative rounded-3xl p-5 sm:p-7 overflow-hidden backdrop-blur-md border"
      style={{
        background:
          "linear-gradient(155deg, rgba(255, 251, 240, 0.78) 0%, rgba(244, 236, 215, 0.45) 100%)",
        borderColor: "rgba(218, 207, 186, 0.5)",
        boxShadow: "0 18px 50px -28px rgba(83,109,83,0.22), 0 1px 0 rgba(255,255,255,0.65) inset",
      }}
      data-testid="calendar-month-view"
    >
      <div className="flex items-center justify-between mb-5">
        <div>
          <p className="tk-label">Mes</p>
          <h3 className="font-outfit text-xl text-ink-900 mt-0.5 capitalize" data-testid="month-title">
            {headerLabel}
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <NavButton onClick={() => goto(-1)} testId="month-prev"><ChevronLeft size={16} /></NavButton>
          <NavButton onClick={() => goto(1)} testId="month-next"><ChevronRight size={16} /></NavButton>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-2 mb-2" aria-hidden="true">
        {["L", "M", "M", "J", "V", "S", "D"].map((d, i) => (
          <div
            key={i}
            className="font-dmsans text-[10px] tracking-[0.18em] uppercase text-center text-ink-600/70"
          >
            {d}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-2 sm:gap-3">
        {matrix.flat().map((cell) => {
          const data = byIso.get(cell.iso);
          const overall = data?.overall ?? null;
          const isToday = cell.iso === today;
          const isSelected = selectedDate === cell.iso;
          const color = calendarColor(overall);
          const inactive = !cell.inMonth;

          return (
            <button
              key={cell.iso}
              type="button"
              onClick={() => onSelect(cell.iso)}
              className={`group relative aspect-square rounded-full flex items-center justify-center transition-transform duration-200 ${
                inactive ? "opacity-25 hover:opacity-40" : "hover:scale-[1.06]"
              } focus:outline-none focus:ring-2 focus:ring-sage-400/60`}
              style={{
                background: overall != null ? color : "rgba(255, 251, 240, 0.55)",
                border: overall != null
                  ? "1px solid rgba(255,255,255,0.55)"
                  : "1px dashed rgba(180,165,140,0.45)",
                boxShadow: isSelected
                  ? "0 0 0 2px #2A9D8F, 0 8px 20px -8px rgba(42,157,143,0.45)"
                  : isToday
                  ? "0 0 0 2px rgba(42,157,143,0.45)"
                  : overall != null
                  ? "0 6px 16px -10px rgba(83,109,83,0.45)"
                  : "none",
              }}
              data-testid={`month-cell-${cell.iso}`}
            >
              <span
                className={`font-outfit text-sm sm:text-base ${
                  overall != null && overall >= 4 ? "text-ink-900" : "text-ink-900/85"
                }`}
              >
                {cell.date.getDate()}
              </span>
              {data?.has_reflection && (
                <MessageCircle
                  size={9}
                  className="absolute"
                  style={{ top: 6, right: 6, color: "rgba(44, 53, 50, 0.65)" }}
                />
              )}
              {isToday && !isSelected && (
                <span
                  aria-hidden="true"
                  className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full"
                  style={{ background: "#2A9D8F" }}
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function NavButton({ onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="h-9 w-9 rounded-full flex items-center justify-center text-ink-600 hover:text-ink-900 transition border backdrop-blur-md"
      style={{
        background: "rgba(255, 251, 240, 0.7)",
        borderColor: "rgba(218, 207, 186, 0.55)",
      }}
      data-testid={testId}
    >
      {children}
    </button>
  );
}
